// A person's workspace (slice 05, issue 745; design/07-ui-shell.md §7, §8;
// design/26-binding-decisions.md 9, 21, 31, 38).
//
// One document per person per BASE-SYSTEM LABEL: presets never carry between
// systems and always carry between tables of the same one, so the label the
// DM sets on a table is the whole key. The document is the person's own —
// no route reads or writes anyone else's, by construction — and the server
// validates it with this module before storing it, so a browser cannot store
// a shape the next browser cannot read.
//
// The dock layout inside a view is dockview's own serialised form and is
// OPAQUE here: bounded in size, an object, nothing more. What the shell reads
// out of it lives in the client. The stock arrangement is not stored: `dock:
// null` means "build the stock preset", so changing what a new person starts
// with is a change to one constant, never a migration.
import { isPaletteName } from '../table/palette.js';
import { storedLength } from '../state/stored.js';
import { parseStatBlockLayout, copyStatBlockLayout } from './statBlock.js';
export const SLOTS = ['left', 'right', 'bottom', 'center'];
export const VIEWS = ['main', 'second'];
/** The stock arrangement is itself a preset, and this is its id; a saved preset may not take it. */
export const STOCK_PRESET_ID = 'stock';
/** The Fight layout (qa-runs#404), shipped beside the stock one: a saved preset may not take its id either. */
export const FIGHT_PRESET_ID = 'fight';
export const SHIPPED_PRESET_IDS = [STOCK_PRESET_ID, FIGHT_PRESET_ID];
export const isShippedPreset = (id) => SHIPPED_PRESET_IDS.includes(id);
// ── The side bars' tabs (qa-runs#404; design/07 §1, §3) ──────────────────────
/** The bars that hold a list of tabs; the bottom bar and the second window's centre keep one tool. */
export const TABBED_SLOTS = ['left', 'right'];
export const isTabbedSlot = (slot) => TABBED_SLOTS.includes(slot);
/** A tool's panel in the serialised dock is `tool:<id>`; anything else (the board, an empty bar's `+`) is not a tab. */
export const TOOL_PANEL_PREFIX = 'tool:';
export const shippedDock = (bars) => ({ shipped: bars });
const toolOf = (panel) => typeof panel === 'string' && panel.startsWith(TOOL_PANEL_PREFIX) && panel.length > TOOL_PANEL_PREFIX.length ? panel.slice(TOOL_PANEL_PREFIX.length) : null;
const emptyBars = () => ({ left: { tabs: [], showing: null }, right: { tabs: [], showing: null }, bottom: { tabs: [], showing: null }, center: { tabs: [], showing: null } });
/**
 * What each bar holds in a stored dock (qa-runs#404): a shipped preset's bars, or dockview's serialised grid read by its
 * groups' stable ids — each group's panels in tab order and its active one. A layout stored before tabs had one panel
 * per group and reads as one tab per bar. A bar the layout does not mention reads as empty; `null` (the stock) is null.
 */
export function barsOfDock(dock) {
    if (!dock)
        return null;
    const out = emptyBars();
    if (isRecord(dock.shipped)) {
        for (const slot of SLOTS) {
            const list = dock.shipped[slot];
            if (!Array.isArray(list))
                continue;
            const tabs = list.filter((t) => typeof t === 'string' && t.length > 0);
            out[slot] = { tabs: isTabbedSlot(slot) ? tabs : tabs.slice(0, 1), showing: tabs[0] ?? null };
        }
        return out;
    }
    const grid = dock.grid;
    const walk = (node) => {
        if (!isRecord(node))
            return;
        if (node.type === 'branch' && Array.isArray(node.data)) {
            node.data.forEach(walk);
            return;
        }
        if (node.type !== 'leaf' || !isRecord(node.data))
            return;
        const leaf = node.data;
        if (typeof leaf.id !== 'string' || !SLOTS.includes(leaf.id) || !Array.isArray(leaf.views))
            return;
        const tabs = [];
        for (const v of leaf.views) {
            const t = toolOf(v);
            if (t && !tabs.includes(t))
                tabs.push(t);
        }
        const active = toolOf(leaf.activeView);
        out[leaf.id] = { tabs, showing: active && tabs.includes(active) ? active : tabs[0] ?? null };
    };
    walk(grid?.root);
    return out;
}
/** The built-in tab icons (qa-runs#404): no uploads. A glyph every screen draws, and the name a screen reader is told. */
export const TAB_ICONS = [
    { id: 'swords', glyph: '⚔', name: 'Crossed swords' },
    { id: 'die', glyph: '⚀', name: 'Die' },
    { id: 'hourglass', glyph: '⌛', name: 'Hourglass' },
    { id: 'flag', glyph: '⚑', name: 'Flag' },
    { id: 'star', glyph: '★', name: 'Star' },
    { id: 'skull', glyph: '☠', name: 'Skull' },
    { id: 'quill', glyph: '✎', name: 'Quill' },
    { id: 'list', glyph: '☰', name: 'List' },
    { id: 'eye', glyph: '◉', name: 'Eye' },
    { id: 'letter', glyph: '✉', name: 'Letter' },
    { id: 'music', glyph: '♫', name: 'Music' },
    { id: 'crown', glyph: '♛', name: 'Crown' },
    { id: 'heart', glyph: '♥', name: 'Heart' },
    { id: 'tower', glyph: '♜', name: 'Tower' },
    { id: 'face', glyph: '☺', name: 'Face' },
    { id: 'sun', glyph: '☀', name: 'Sun' },
];
export const isTabIcon = (v) => typeof v === 'string' && TAB_ICONS.some((i) => i.id === v);
export const tabIcon = (id) => TAB_ICONS.find((i) => i.id === id) ?? null;
/** The icons as stored and sent, strictly: a tool id to an icon of the set; anything else is null and refuses the document. */
export function parseTabIcons(v) {
    if (!isRecord(v) || Object.keys(v).length > LAYOUT_LIMITS.icons)
        return null;
    const out = {};
    for (const [tool, icon] of Object.entries(v)) {
        if (!TOOL_ID_RE.test(tool) || !isTabIcon(icon))
            return null;
        out[tool] = icon;
    }
    return out;
}
const TOOL_ID_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
/** The tab names as stored and sent (qa-runs#1431): a tool id to a trimmed name of 1–24 characters; anything else is null. */
export function parseTabNames(v) {
    if (!isRecord(v) || Object.keys(v).length > LAYOUT_LIMITS.names)
        return null;
    const out = {};
    for (const [tool, name] of Object.entries(v)) {
        const parsed = TOOL_ID_RE.test(tool) ? parseName(name, LAYOUT_LIMITS.tabName) : null;
        if (parsed === null)
            return null;
        out[tool] = parsed;
    }
    return out;
}
export const PANEL_KINDS = ['monster', 'pc', 'scenery', 'scene'];
export const HOTBAR_DEFAULTS = { size: 60, opacity: 90, count: null, rows: 1, labels: 'hover', position: 'bottom', locked: false, next: 'Tab', prev: 'Shift+Tab', past10: 'alt' };
export const HOTBAR_LIMITS = { size: [10, 200], opacity: [10, 100], count: [10, 40] };
const HOTBAR_KEY_RE = /^(Ctrl\+)?(Alt\+)?(Shift\+)?([A-Za-z0-9]|F[1-9][0-2]?|Tab|[-=,./;'`\[\]])$/;
const inRange = (v, [lo, hi]) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
/** The hot bar's settings as stored, strictly: null when any field is outside its shape. */
export function parseHotbarPrefs(v) {
    if (!isRecord(v))
        return null;
    const out = {};
    for (const [k, x] of Object.entries(v)) {
        if (k === 'size' && inRange(x, HOTBAR_LIMITS.size))
            out.size = x;
        else if (k === 'opacity' && inRange(x, HOTBAR_LIMITS.opacity))
            out.opacity = x;
        else if (k === 'count' && (x === null || inRange(x, HOTBAR_LIMITS.count)))
            out.count = x;
        else if (k === 'rows' && (x === 1 || x === 2 || x === 3))
            out.rows = x;
        else if (k === 'labels' && (x === 'hover' || x === 'always'))
            out.labels = x;
        else if (k === 'position' && (x === 'top' || x === 'bottom'))
            out.position = x;
        else if (k === 'locked' && typeof x === 'boolean')
            out.locked = x;
        else if (k === 'past10' && (x === 'alt' || x === 'none'))
            out.past10 = x;
        else if ((k === 'next' || k === 'prev') && typeof x === 'string' && x.length <= 24 && HOTBAR_KEY_RE.test(x))
            out[k] = x;
        else
            return null;
    }
    return out;
}
export const DICE_SECTIONS = ['attacks', 'saves', 'skills', 'abilities', 'dice'];
export const PANEL_LIMITS = { sectionsPerKind: 60 };
const SECTION_KEY_RE = /^[a-z0-9][a-z0-9:._-]{0,99}$/;
/** The panel's settings as stored and sent, strictly: anything outside the shape is null, and refuses the document. */
export function parsePanelPrefs(v) {
    if (!isRecord(v))
        return null;
    const out = {};
    for (const key of Object.keys(v))
        if (key !== 'open' && key !== 'statBlock' && key !== 'colourMatch' && key !== 'dice' && key !== 'hotbar')
            return null;
    if (v.open !== undefined) {
        if (!isRecord(v.open))
            return null;
        const open = {};
        for (const kind of Object.keys(v.open)) {
            if (!PANEL_KINDS.includes(kind))
                return null;
            const rec = v.open[kind];
            if (!isRecord(rec) || Object.keys(rec).length > PANEL_LIMITS.sectionsPerKind)
                return null;
            const kept = {};
            for (const [k, b] of Object.entries(rec)) {
                if (!SECTION_KEY_RE.test(k) || typeof b !== 'boolean')
                    return null;
                kept[k] = b;
            }
            open[kind] = kept;
        }
        out.open = open;
    }
    if (v.statBlock !== undefined) {
        const layout = parseStatBlockLayout(v.statBlock);
        if (!layout)
            return null;
        out.statBlock = copyStatBlockLayout(layout);
    }
    if (v.colourMatch !== undefined) {
        if (typeof v.colourMatch !== 'boolean')
            return null;
        out.colourMatch = v.colourMatch;
    }
    if (v.dice !== undefined) {
        if (!isRecord(v.dice))
            return null;
        const dice = {};
        for (const [k, b] of Object.entries(v.dice)) {
            if (!DICE_SECTIONS.includes(k) || typeof b !== 'boolean')
                return null;
            dice[k] = b;
        }
        out.dice = dice;
    }
    if (v.hotbar !== undefined) {
        const hotbar = parseHotbarPrefs(v.hotbar);
        if (!hotbar)
            return null;
        out.hotbar = hotbar;
    }
    return out;
}
export const LAYOUT_LIMITS = {
    presets: 20,
    presetName: 40,
    menus: 12,
    menuName: 24,
    menuTools: 40,
    id: 40,
    toolId: 40,
    /** One view's dock layout, serialised. */
    dockBytes: 32 * 1024,
    /** The whole document, as the database counts it (jsonb's text, `storedLength`; vtt/003-layouts.xml's 131,072). */
    docBytes: 128 * 1024,
    baseSystem: 80,
    /** Tools with an icon picked for their tab (qa-runs#404). */
    icons: 40,
    /** Tools with a name given to their tab, and that name's length (qa-runs#1431). */
    names: 40,
    tabName: 24,
};
const ID_RE = /^[A-Za-z0-9_-]{1,40}$/;
export function emptyLayoutDoc() {
    return {
        v: 1,
        menus: [],
        presets: [],
        current: { main: { dock: null, preset: STOCK_PRESET_ID }, second: { dock: null, preset: STOCK_PRESET_ID } },
    };
}
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function parseName(v, max) {
    if (typeof v !== 'string')
        return null;
    const name = v.trim();
    if (name.length === 0 || name.length > max || /[\u0000-\u001f]/.test(name))
        return null;
    return name;
}
function parseDock(v) {
    if (v === null)
        return null;
    if (!isRecord(v))
        return undefined;
    if (storedLength(v) > LAYOUT_LIMITS.dockBytes)
        return undefined;
    return v;
}
function parseMenu(v, seen) {
    if (!isRecord(v))
        return null;
    if (typeof v.id !== 'string' || !ID_RE.test(v.id) || seen.has(v.id))
        return null;
    const name = parseName(v.name, LAYOUT_LIMITS.menuName);
    if (!name)
        return null;
    if (!Array.isArray(v.tools) || v.tools.length > LAYOUT_LIMITS.menuTools)
        return null;
    const tools = [];
    for (const t of v.tools) {
        if (typeof t !== 'string' || t.length === 0 || t.length > LAYOUT_LIMITS.toolId || tools.includes(t))
            return null;
        tools.push(t);
    }
    seen.add(v.id);
    return { id: v.id, name, tools };
}
function parsePreset(v, seen) {
    if (!isRecord(v))
        return null;
    if (typeof v.id !== 'string' || !ID_RE.test(v.id) || isShippedPreset(v.id) || seen.has(v.id))
        return null;
    const name = parseName(v.name, LAYOUT_LIMITS.presetName);
    if (!name)
        return null;
    if (!isRecord(v.views))
        return null;
    const views = {};
    for (const key of Object.keys(v.views)) {
        if (!VIEWS.includes(key))
            return null;
        const dock = parseDock(v.views[key]);
        if (dock === undefined)
            return null;
        views[key] = dock;
    }
    if (Object.keys(views).length === 0)
        return null;
    seen.add(v.id);
    return { id: v.id, name, views };
}
function parseViewState(v) {
    if (!isRecord(v))
        return null;
    const dock = parseDock(v.dock);
    if (dock === undefined)
        return null;
    if (typeof v.preset !== 'string' || !ID_RE.test(v.preset))
        return null;
    return { dock, preset: v.preset };
}
/**
 * The document as stored and as sent: anything outside the shape, over a
 * cap, or naming a duplicate reads as null and is refused whole. Names are
 * trimmed; nothing else is coerced.
 */
export function parseLayoutDoc(value) {
    if (!isRecord(value) || value.v !== 1)
        return null;
    if (!Array.isArray(value.menus) || value.menus.length > LAYOUT_LIMITS.menus)
        return null;
    if (!Array.isArray(value.presets) || value.presets.length > LAYOUT_LIMITS.presets)
        return null;
    if (!isRecord(value.current))
        return null;
    const menuIds = new Set();
    const menus = [];
    for (const m of value.menus) {
        const menu = parseMenu(m, menuIds);
        if (!menu)
            return null;
        menus.push(menu);
    }
    const presetIds = new Set();
    const presets = [];
    for (const p of value.presets) {
        const preset = parsePreset(p, presetIds);
        if (!preset)
            return null;
        presets.push(preset);
    }
    const current = {};
    for (const view of VIEWS) {
        const state = parseViewState(value.current[view]);
        if (!state)
            return null;
        current[view] = state;
    }
    for (const key of Object.keys(value.current))
        if (!VIEWS.includes(key))
            return null;
    const panel = value.panel === undefined ? undefined : parsePanelPrefs(value.panel);
    if (panel === null)
        return null;
    const icons = value.icons === undefined ? undefined : parseTabIcons(value.icons);
    if (icons === null)
        return null;
    const names = value.names === undefined ? undefined : parseTabNames(value.names);
    if (names === null)
        return null;
    const doc = {
        v: 1, menus, presets, current, ...(panel ? { panel } : {}), ...(icons ? { icons } : {}), ...(names ? { names } : {}),
    };
    if (storedLength(doc) > LAYOUT_LIMITS.docBytes)
        return null;
    return doc;
}
/** The text sizes a person may pick (issue 1174). */
export const SHELL_TEXT_SIZES = [100, 125, 150, 175, 200];
export const isShellTextSize = (v) => typeof v === 'number' && SHELL_TEXT_SIZES.includes(v);
export const isTypeface = (v) => v === 'standard' || v === 'reading';
/** The look fields a settings value carries, from a stored or chosen size and typeface: the defaults are absent. */
export function textLook(size, face) {
    return { ...(isShellTextSize(size) && size !== 100 ? { textSize: size } : {}), ...(face === 'reading' ? { typeface: 'reading' } : {}) };
}
export const DEFAULT_SETTINGS = { onboardingSeen: false, offerPresets: true, confirmDelete: true };
/** A partial update; an unknown key or a non-boolean value refuses the whole patch. */
export function parseSettingsPatch(value) {
    if (!isRecord(value))
        return null;
    const patch = {};
    for (const key of Object.keys(value)) {
        if (key === 'palette') {
            if (!isPaletteName(value[key]))
                return null;
            patch.palette = value[key];
            continue;
        }
        if (key === 'textSize') {
            if (!isShellTextSize(value[key]))
                return null;
            patch.textSize = value[key];
            continue;
        }
        if (key === 'typeface') {
            if (!isTypeface(value[key]))
                return null;
            patch.typeface = value[key];
            continue;
        }
        if (key !== 'onboardingSeen' && key !== 'offerPresets' && key !== 'confirmDelete' && key !== 'outlines' && key !== 'statusIcons')
            return null;
        if (typeof value[key] !== 'boolean')
            return null;
        patch[key] = value[key];
    }
    return patch;
}
/** The label a table's layouts are keyed by: the DM's base-system label, or the empty string while unset. */
export function layoutKey(baseSystem) {
    return typeof baseSystem === 'string' ? baseSystem : '';
}
export const DEVICE_CLASSES = ['desktop', 'tablet'];
export const isDeviceClass = (v) => v === 'desktop' || v === 'tablet';
export function isLayoutKey(value) {
    return typeof value === 'string' && value.length <= LAYOUT_LIMITS.baseSystem && !/[\u0000-\u001f]/.test(value);
}
//# sourceMappingURL=layout.js.map